/**
 * Bridges the DS `DateField` (which stores a native `YYYY-MM-DD` string) and the
 * domain (which uses `Date`). Use these at the form↔domain boundary:
 *   - `toDateInputValue` when building form values from an entity
 *   - `parseDateInput` when building the domain payload on save
 *
 * Date-only granularity, parsed in local time (no UTC shift).
 */

export function toDateInputValue(value: Date | string | null | undefined): string {
  if (value == null || value === '') return ''
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function parseDateInput(value: string | null | undefined): Date | null {
  if (!value) return null
  const [y, m, d] = value.split('-').map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d)
}

/** True when the date-only value is strictly after today (local time). */
export function isAfterToday(value: string | Date | null | undefined): boolean {
  const input = typeof value === 'string' ? value : toDateInputValue(value)
  const parsed = parseDateInput(input)
  if (!parsed) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return parsed.getTime() > today.getTime()
}

/** Localized date-only (pt-BR) for read-only display. Empty string when absent. */
export function formatDateBR(value: Date | string | null | undefined): string {
  if (value == null || value === '') return ''
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('pt-BR')
}

/**
 * Business calendar date (pt-BR) from a UTC-midnight (or ISO date) value.
 * Uses UTC Y/M/D so BRT does not shift the day (ledger `movementDate`).
 * Do not use for real timestamps — prefer `formatDateTimeBR` / `formatDateBR`.
 */
export function formatBusinessDateBR(value: Date | string | null | undefined): string {
  if (value == null || value === '') return ''
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const day = String(d.getUTCDate()).padStart(2, '0')
  const month = String(d.getUTCMonth() + 1).padStart(2, '0')
  const year = d.getUTCFullYear()
  return `${day}/${month}/${year}`
}

/** Localized date+time (pt-BR) for read-only display. Empty string when absent. */
export function formatDateTimeBR(value: Date | string | null | undefined): string {
  if (value == null || value === '') return ''
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('pt-BR')
}

/**
 * Bridges DS `DateField` `pickerType="datetime-local"` (`YYYY-MM-DDTHH:mm`)
 * and domain `Date`. Local time, no UTC shift.
 */
export function toDateTimeLocalInputValue(value: Date | string | null | undefined): string {
  if (value == null || value === '') return ''
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const h = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${day}T${h}:${min}`
}

export function parseDateTimeLocalInput(value: string | null | undefined): Date | null {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Bridges DS `DateField pickerType="time"` (`HH:mm`) and domain minute-of-day
 * (0..1439). Used by work-calendar jornada / shift / exception hours.
 */
export function toTimeInputValue(minuteOfDay: number | null | undefined): string {
  if (minuteOfDay == null || !Number.isFinite(minuteOfDay)) return ''
  const clamped = Math.max(0, Math.min(1439, Math.floor(minuteOfDay)))
  const h = Math.floor(clamped / 60)
  const min = clamped % 60
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

export function parseTimeInput(value: string | null | undefined): number | null {
  if (!value) return null
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null
  return hours * 60 + minutes
}

/**
 * YYYY-MM-DD from a UTC-midnight business date. Pair with {@link parseBusinessDateInput}.
 * Do not use {@link toDateInputValue} here — BRT would shift the day.
 */
export function toBusinessDateInputValue(value: Date | string | null | undefined): string {
  if (value == null || value === '') return ''
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** UTC-midnight `Date` from a DateField `YYYY-MM-DD` (business calendar date). */
export function parseBusinessDateInput(value: string | null | undefined): Date | null {
  if (!value) return null
  const [y, m, d] = value.split('-').map(Number)
  if (!y || !m || !d) return null
  return new Date(Date.UTC(y, m - 1, d))
}
